/**
 * Derive the xlsx filename for a weekly rollup download / Approve & Send
 * attachment. Matches the convention Joey already uses for the same files
 * in his existing workflow:
 *
 *   "PCO 004.6 - Weekly Rollup WE 3.21.26.xlsx"
 *
 * Lives in `shared` so the API endpoint (download + submission), the
 * sample-render admin endpoint, and any future frontend filename hint
 * all produce the same string for the same input. Joey's existing files
 * land alongside ours and sort correctly in his folder.
 *
 * Components:
 *   - "PCO {n}"        — pco_number, left-padded to 3 digits when the
 *                        number is purely numeric. Non-numeric values
 *                        (e.g. "4A") render as-is.
 *   - ".{m}"           — submission_number (the per-submission billing
 *                        sequence from the owning submission). Omitted when
 *                        null — legacy rows without a submission render as
 *                        "PCO 004 - Weekly Rollup …".
 *   - "WE {m}.{d}.{yy}" — week_ending_date in M.D.YY format with no
 *                        leading zeros on month or day, two-digit year.
 *                        Matches Joey's hand-typed format.
 *
 * Output is sanitized for filesystem + Content-Disposition safety
 * (illegal characters stripped, whitespace collapsed).
 */
import { padPcoNumber } from './pco';

export interface RollupXlsxFilenameInput {
  /** Free-form pco_number from the DB (text column). */
  pco_number: string;
  /** Per-submission billing sequence (the "6" in 002.6) from the owning submission.
   *  Null renders "PCO 004 - Weekly Rollup …" (defensive; app always sets it). */
  submission_number?: string | null;
  /** 'YYYY-MM-DD' as stored in the DB. */
  week_ending_date: string;
}

// Strip control chars + characters that break Windows/macOS/Linux paths
// and HTTP Content-Disposition headers.
// eslint-disable-next-line no-control-regex
const ILLEGAL = /[\x00-\x1f"\\/:*?<>|]/g;
const COLLAPSE_WS = /\s+/g;
const MAX_LEN = 100;

export function deriveRollupXlsxFilename(input: RollupXlsxFilenameInput): string {
  // '000' fallback keeps the filename valid if pco_number is ever blank
  // (the column is NOT NULL, so this is purely defensive).
  const padded = padPcoNumber(input.pco_number) || '000';
  const sub = input.submission_number ? `.${input.submission_number}` : '';
  const we = toShortDate(input.week_ending_date);
  const body = `PCO ${padded}${sub} - Weekly Rollup WE ${we}`;
  return sanitize(body) + '.xlsx';
}

// Exported since 2026-08-13: the GC-facing email SUBJECT now prints the week
// in this same dotted form, and two independent implementations of "the
// short date" had already drifted on input tolerance once. One function,
// every surface.
export function toShortDate(iso: string): string {
  // Only transform when the input is genuinely YYYY-MM-DD. Anything else
  // falls back to the raw string so an upstream bug surfaces in the
  // filename instead of becoming "NaN.NaN.ot".
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  return `${parseInt(m!, 10)}.${parseInt(d!, 10)}.${y!.slice(-2)}`;
}

function sanitize(s: string): string {
  return s.replace(ILLEGAL, '').replace(COLLAPSE_WS, ' ').trim().slice(0, MAX_LEN);
}
